/**
 * Deadline Reminder Settings Component
 * 
 * Allows users to configure email reminder preferences for 
 * the Expiration & Deadline Radar feature
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { getCategoryDeadlineTypes, defaultNotifyTypesFor } from '../../Data/deadlineTypeCatalog';

// Immigration keeps its existing settings list unchanged (keys match the legacy
// notifyTypes shape). Non-immigration categories come from the catalog above.
const IMMIGRATION_SETTINGS_TYPES = [
  { key: 'passport', label: 'Passport' },
  { key: 'visa', label: 'Visa' },
  { key: 'i94', label: 'I-94' },
  { key: 'ead', label: 'EAD' },
  { key: 'ap', label: 'Advance Parole' },
  { key: 'greenCard', label: 'Green Card' },
  { key: 'medicalExam', label: 'Medical Exam' },
  { key: 'feePayment', label: 'Fee Payment' },
  { key: 'rfe', label: 'RFE Deadline' },
  { key: 'other', label: 'Other' },
];

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const Section = styled.div`
  background: rgba(99, 102, 241, 0.05);
  border: none;
  border-radius: 12px;
  border-left: 3px solid rgba(234, 88, 12, 0.6);
  padding: 24px;
  margin-bottom: 20px;
  backdrop-filter: blur(8px);
  animation: ${fadeIn} 0.3s ease-out;
`;

const SectionTitle = styled.h2`
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  padding: 10px 18px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(234, 88, 12, 0.3);
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(234, 88, 12, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%);
  letter-spacing: 0.3px;
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 24px;
`;

const SettingsGrid = styled.div`
  display: grid;
  gap: 20px;
`;

const SettingsCard = styled.div`
  background: rgba(15, 20, 35, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.1);
  border-radius: 12px;
  padding: 20px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  
  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #e0e7ff;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const Toggle = styled.button`
  width: 48px;
  height: 26px;
  border-radius: 13px;
  border: none;
  background: ${props => props.$active ? 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)' : 'rgba(255, 255, 255, 0.1)'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  
  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${props => props.$active ? '24px' : '3px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
  
  &:hover {
    background: ${props => props.$active ? 'linear-gradient(135deg, #c2410c 0%, #ea580c 100%)' : 'rgba(255, 255, 255, 0.15)'};
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
`;

const Select = styled.select`
  width: 100%;
  background: rgba(25, 30, 45, 0.9);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(234, 88, 12, 0.5);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
  
  option {
    background: #1a1f35;
  }
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
  margin-top: 12px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
  
  input {
    width: 16px;
    height: 16px;
    accent-color: #ea580c;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const Tag = styled.button`
  padding: 6px 12px;
  border-radius: 16px;
  border: 1px solid ${props => props.$active ? 'rgba(234, 88, 12, 0.5)' : 'rgba(255, 255, 255, 0.15)'};
  background: ${props => props.$active ? 'rgba(234, 88, 12, 0.2)' : 'transparent'};
  color: ${props => props.$active ? '#fb923c' : 'rgba(255, 255, 255, 0.6)'};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: rgba(234, 88, 12, 0.4);
    background: rgba(234, 88, 12, 0.1);
    color: #fb923c;
  }
`;

const SaveButton = styled.button`
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 12px 28px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
  margin-top: 20px;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #c2410c 0%, #ea580c 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(234, 88, 12, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 16px;
  font-size: 14px;
  animation: ${fadeIn} 0.3s ease-out;
  
  ${props => props.$type === 'success' && `
    background: rgba(34, 197, 94, 0.1);
    border: 1px solid rgba(34, 197, 94, 0.3);
    color: #4ade80;
  `}
  
  ${props => props.$type === 'error' && `
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #f87171;
  `}
`;

const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: rgba(255, 255, 255, 0.6);
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

// ============================================
// ICONS
// ============================================

const Icons = {
  bell: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  clock: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>,
  calendar: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  filter: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"/></svg>,
};

// ============================================
// COMPONENT
// ============================================

const DeadlineReminderSettings = () => {
  const apiClient = useAuthenticatedAxios();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [category, setCategory] = useState('');
  
  const [settings, setSettings] = useState({
    enabled: true,
    frequency: 'daily',
    weeklyDigestDay: 1,
    digestHour: 9,
    notifyTypes: {
      passport: true,
      visa: true,
      i94: true,
      ead: true,
      ap: true,
      greenCard: true,
      medicalExam: true,
      feePayment: true,
      rfe: true,
      other: true
    },
    reminderDays: [90, 60, 30, 14, 7, 1],
    notifyPriorities: {
      critical: true,
      high: true,
      medium: true,
      low: false
    }
  });

  // Test email state
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testStatus, setTestStatus] = useState(null);

  // Fetch current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await apiClient.get('/profile/deadline-reminders');
        const cat = response.data?.category || '';
        setCategory(cat);
        if (response.data?.deadlineReminderSettings) {
          const fetched = response.data.deadlineReminderSettings;
          // For a non-immigration category, ensure this category's deadline types are
          // present (default enabled) so the checkboxes render as on for a first-time user.
          const catDefaults = defaultNotifyTypesFor(cat); // null for immigration
          setSettings(prev => ({
            ...prev,
            ...fetched,
            notifyTypes: {
              ...(catDefaults || {}),
              ...(fetched.notifyTypes || {})
            }
          }));
        }
      } catch (error) {
        console.error('Error fetching deadline reminder settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [apiClient]);

  // Handle saving settings
  const handleSave = async () => {
    setSaving(true);
    setStatus(null);

    try {
      await apiClient.put('/profile/deadline-reminders', {
        deadlineReminderSettings: settings
      });
      setStatus({ type: 'success', message: 'Settings saved successfully!' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setStatus({ type: 'error', message: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Handle toggle change
  const handleToggle = (key, subKey = null) => {
    setSettings(prev => {
      if (subKey) {
        return {
          ...prev,
          [key]: {
            ...prev[key],
            [subKey]: !prev[key][subKey]
          }
        };
      }
      return {
        ...prev,
        [key]: !prev[key]
      };
    });
  };

  // Handle select change
  const handleSelect = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle reminder days toggle
  const handleReminderDayToggle = (days) => {
    setSettings(prev => {
      const currentDays = prev.reminderDays || [];
      if (currentDays.includes(days)) {
        return {
          ...prev,
          reminderDays: currentDays.filter(d => d !== days)
        };
      }
      return {
        ...prev,
        reminderDays: [...currentDays, days].sort((a, b) => b - a)
      };
    });
  };

  // Handle sending test email
  const handleSendTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      setTestStatus({ type: 'error', message: 'Please enter a valid email address' });
      return;
    }

    setSendingTest(true);
    setTestStatus(null);

    try {
      // Call the server endpoint which will proxy to SMTP service
      await apiClient.post('/profile/deadline-reminders/test', {
        email: testEmail
      });

      setTestStatus({ type: 'success', message: `Test email sent to ${testEmail}! Check your inbox.` });
      setTestEmail('');
    } catch (error) {
      console.error('Error sending test email:', error);
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message;
      setTestStatus({ type: 'error', message: `Failed to send test email: ${errorMessage}` });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <Section>
        <SectionTitle>{Icons.bell} Deadline Reminders</SectionTitle>
        <LoadingOverlay>Loading settings...</LoadingOverlay>
      </Section>
    );
  }

  const dayOptions = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${i.toString().padStart(2, '0')}:00 ${i < 12 ? 'AM' : 'PM'}`
  }));

  const reminderDayOptions = [180, 90, 60, 45, 30, 21, 14, 7, 3, 1];

  // Deadline types shown here depend on the adviser's category. Immigration keeps its
  // existing list; other categories get their own from the catalog.
  const deadlineTypeList = getCategoryDeadlineTypes(category) || IMMIGRATION_SETTINGS_TYPES;

  return (
    <Section>
      <SectionTitle>{Icons.bell} Deadline Reminders</SectionTitle>
      
      <Description>
        Configure how you receive email notifications about upcoming deadlines and expirations
        from your Deadline Radar. The deadline types below are tailored to your practice area,
        so you stay on top of what matters for your clients.
      </Description>

      <SettingsGrid>
        {/* Master Toggle */}
        <SettingsCard>
          <CardHeader>
            <h3>{Icons.bell} Email Notifications</h3>
            <Toggle 
              $active={settings.enabled}
              onClick={() => handleToggle('enabled')}
            />
          </CardHeader>
          <Description style={{ margin: 0, fontSize: '13px' }}>
            {settings.enabled 
              ? 'Email reminders are enabled. You will receive notifications based on your settings below.'
              : 'Email reminders are disabled. Enable to receive deadline notifications.'}
          </Description>
        </SettingsCard>

        {settings.enabled && (
          <>
            {/* Frequency Settings */}
            <SettingsCard>
              <CardHeader>
                <h3>{Icons.clock} Notification Frequency</h3>
              </CardHeader>
              
              <Label>How often should we send reminder emails?</Label>
              <Select 
                value={settings.frequency}
                onChange={(e) => handleSelect('frequency', e.target.value)}
              >
                <option value="realtime">Real-time (immediate alerts)</option>
                <option value="daily">Daily Digest</option>
                <option value="weekly">Weekly Summary</option>
                <option value="off">Don't send emails</option>
              </Select>

              {(settings.frequency === 'daily' || settings.frequency === 'weekly') && (
                <FormRow style={{ marginTop: '16px' }}>
                  {settings.frequency === 'weekly' && (
                    <div>
                      <Label>Send on</Label>
                      <Select 
                        value={settings.weeklyDigestDay}
                        onChange={(e) => handleSelect('weeklyDigestDay', parseInt(e.target.value))}
                      >
                        {dayOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label>At time (UTC)</Label>
                    <Select 
                      value={settings.digestHour}
                      onChange={(e) => handleSelect('digestHour', parseInt(e.target.value))}
                    >
                      {hourOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Select>
                  </div>
                </FormRow>
              )}
            </SettingsCard>

            {/* Deadline Types (personalized per adviser category) */}
            <SettingsCard>
              <CardHeader>
                <h3>{Icons.calendar} Deadline Types</h3>
              </CardHeader>
              <Label>Which types of deadlines/expirations should trigger notifications?</Label>

              <CheckboxGrid>
                {deadlineTypeList.map(t => (
                  <CheckboxLabel key={t.key}>
                    <input
                      type="checkbox"
                      checked={settings.notifyTypes?.[t.key] ?? true}
                      onChange={() => handleToggle('notifyTypes', t.key)}
                    />
                    {t.label}
                  </CheckboxLabel>
                ))}
              </CheckboxGrid>
            </SettingsCard>

            {/* Reminder Thresholds */}
            <SettingsCard>
              <CardHeader>
                <h3>{Icons.filter} Reminder Thresholds</h3>
              </CardHeader>
              <Label>How many days before expiry should we notify you?</Label>
              
              <TagsContainer>
                {reminderDayOptions.map(days => (
                  <Tag 
                    key={days}
                    $active={settings.reminderDays?.includes(days)}
                    onClick={() => handleReminderDayToggle(days)}
                  >
                    {days} {days === 1 ? 'day' : 'days'}
                  </Tag>
                ))}
              </TagsContainer>
            </SettingsCard>

            {/* Priority Filter */}
            <SettingsCard>
              <CardHeader>
                <h3>{Icons.filter} Priority Filter</h3>
              </CardHeader>
              <Label>Which priority levels should trigger notifications?</Label>
              
              <CheckboxGrid>
                <CheckboxLabel>
                  <input 
                    type="checkbox" 
                    checked={settings.notifyPriorities.critical}
                    onChange={() => handleToggle('notifyPriorities', 'critical')}
                  />
                  🔴 Critical
                </CheckboxLabel>
                <CheckboxLabel>
                  <input 
                    type="checkbox" 
                    checked={settings.notifyPriorities.high}
                    onChange={() => handleToggle('notifyPriorities', 'high')}
                  />
                  🟠 High
                </CheckboxLabel>
                <CheckboxLabel>
                  <input 
                    type="checkbox" 
                    checked={settings.notifyPriorities.medium}
                    onChange={() => handleToggle('notifyPriorities', 'medium')}
                  />
                  🟡 Medium
                </CheckboxLabel>
                <CheckboxLabel>
                  <input 
                    type="checkbox" 
                    checked={settings.notifyPriorities.low}
                    onChange={() => handleToggle('notifyPriorities', 'low')}
                  />
                  ⚪ Low
                </CheckboxLabel>
              </CheckboxGrid>
            </SettingsCard>
          </>
        )}
      </SettingsGrid>

      {status && (
        <StatusMessage $type={status.type}>
          {status.message}
        </StatusMessage>
      )}

      <SaveButton onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save Reminder Settings'}
      </SaveButton>

      {/* Test Email Section */}
      <SettingsCard style={{ marginTop: '24px', borderLeft: '3px solid #22c55e' }}>
        <CardHeader>
          <h3>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            Send Test Email
          </h3>
        </CardHeader>
        <Description style={{ margin: '0 0 16px 0', fontSize: '13px' }}>
          Send a sample deadline reminder email to verify the template looks correct.
          The test will include sample expiration data (1 expired, 1 critical, 2 expiring soon).
        </Description>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <Label>Email Address</Label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="your@email.com"
              style={{
                width: '100%',
                background: 'rgba(25, 30, 45, 0.9)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
                borderRadius: '8px',
                padding: '10px 12px',
                color: '#fff',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <button
            onClick={handleSendTestEmail}
            disabled={sendingTest || !testEmail}
            style={{
              background: sendingTest ? 'rgba(34, 197, 94, 0.3)' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              cursor: sendingTest || !testEmail ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: sendingTest || !testEmail ? 0.6 : 1,
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap'
            }}
          >
            {sendingTest ? 'Sending...' : '📧 Send Test'}
          </button>
        </div>

        {testStatus && (
          <StatusMessage $type={testStatus.type} style={{ marginTop: '12px' }}>
            {testStatus.message}
          </StatusMessage>
        )}
      </SettingsCard>
    </Section>
  );
};

export default DeadlineReminderSettings;

